import { classifyStandaloneNavigation } from "@repo/shared";
import { type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import {
  createPath,
  type NavigateOptions,
  type Navigator,
  parsePath,
  type To,
  UNSAFE_NavigationContext,
  useLocation,
  useNavigate,
} from "react-router";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { log } from "@/lib/logger/browser-logger";

/**
 * QL-262 — the single route-level guard that keeps a standalone user inside
 * the flow. Mounted once, inside `<BrowserRouter>` and above every route.
 *
 * Outside standalone mode it is transparent. In standalone mode every in-app
 * navigation target is classified (`classifyStandaloneNavigation`):
 *
 *   - in-flow (`/clients/:id/flows/:instanceId`, `/process`, …) → proceeds; a
 *     target that DROPPED `standalone=true` is corrected (params re-added), so
 *     a forgotten `withStandaloneParams` becomes a no-op rather than an exit;
 *   - logout (`/login`, `/clear`) → proceeds, so an expired session can still
 *     round-trip through the login page (which carries the link in `?redirect=`);
 *   - external (another origin) → proceeds; documents and partner links are
 *     not "another part of QP";
 *   - out-of-flow (client list, client detail, settings, dashboard, 404 → `/`,
 *     …) → cancelled; when an `internalRedirectUrl` exists the user is sent
 *     there instead, so a stray "leave" becomes "return to the partner".
 *
 * Mechanism — why not `useBlocker`: the app renders a plain `<BrowserRouter>`
 * (`app.tsx`), not a data router, and react-router's `useBlocker` throws
 * outside a data router. In a plain router every client-side navigation —
 * `navigate()` from `useNavigate`, `<Link>` clicks (which call `navigate`),
 * `<Navigate>` redirects and `setSearchParams` — ends in
 * `NavigationContext.navigator.push/replace`. Overriding that context value
 * below therefore intercepts ALL of them at one choke point; `navigator.go`
 * (history back/forward) is left alone because the browser's own back button
 * cannot be intercepted anyway. The one thing that never reaches the
 * navigator is a raw `<a href="/…">` (e.g. a link an advisor typed into a
 * markdown text block), which would do a full page load out of the flow — a
 * capture-phase click listener on `document` applies the same classification
 * to same-origin anchors and cancels/corrects them before the browser (or
 * `<Link>`, which honours `defaultPrevented`) acts.
 */
export function StandaloneNavigationGuard({ children }: { children: ReactNode }) {
  const navigation = useContext(UNSAFE_NavigationContext);
  // The guard's own navigate goes through the OUTER navigator — used only for
  // targets the guard has already resolved.
  const navigate = useNavigate();
  const { search } = useLocation();
  const { standalone, internalRedirectUrl } = useStandaloneMode();

  /**
   * Resolve a navigation target: the (possibly corrected) target to proceed
   * with, or null when the navigation is cancelled.
   */
  const resolve = useCallback(
    (target: string): string | null => {
      if (!standalone) return target;
      const decision = classifyStandaloneNavigation(target, search);
      switch (decision.kind) {
        case "in-flow":
          if (decision.corrected) {
            log.warn(
              { target, corrected: decision.to },
              "standalone mode: navigation dropped the standalone params — corrected",
            );
          }
          return decision.to;
        case "logout":
        case "external":
          return target;
        case "out-of-flow":
          log.warn(
            { target, returnsToPartner: !!internalRedirectUrl },
            "standalone mode: navigation out of the flow cancelled",
          );
          if (internalRedirectUrl) window.location.assign(internalRedirectUrl);
          return null;
      }
    },
    [standalone, search, internalRedirectUrl],
  );

  // Choke point 1 — react-router's navigator (navigate(), <Link>, <Navigate>).
  const navigator = useMemo<Navigator>(() => {
    const through =
      (method: "push" | "replace") => (to: To, state?: unknown, opts?: NavigateOptions) => {
        const target = typeof to === "string" ? to : createPath(to);
        const resolved = resolve(target);
        if (resolved === null) return;
        navigation.navigator[method](resolved === target ? to : parsePath(resolved), state, opts);
      };
    return { ...navigation.navigator, push: through("push"), replace: through("replace") };
  }, [navigation.navigator, resolve]);

  const value = useMemo(() => ({ ...navigation, navigator }), [navigation, navigator]);

  // Choke point 2 — raw same-origin anchors (full page loads) and, as a
  // consequence, <Link> clicks before react-router sees them.
  useEffect(() => {
    if (!standalone) return;
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!(anchor instanceof HTMLAnchorElement)) return;
      // New-tab and download links (PDFs, product documents) are allowed.
      if ((anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      const target = `${url.pathname}${url.search}${url.hash}`;
      const resolved = resolve(target);
      if (resolved === target) return;
      event.preventDefault();
      if (resolved !== null) navigate(resolved);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [standalone, resolve, navigate]);

  return (
    <UNSAFE_NavigationContext.Provider value={value}>{children}</UNSAFE_NavigationContext.Provider>
  );
}
